import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import CardTable from "../components/charts/CardTable";
import { getRacesToday, getRaceCard, getBetfairPrices } from "../api";
import { todayStr } from "../hooks/dateUtils";

/* Pick today's feature race: highest class (lowest class_num), latest time as tiebreak */
function pickFeaturedRace(races) {
  const today = todayStr();
  const todayRaces = races.filter((r) => (r.race_date || "").slice(0, 10) === today);
  if (todayRaces.length === 0) return null;

  // Lowest class_num first (Class 1 = best), latest time as tiebreak (feature race = afternoon headline)
  todayRaces.sort((a, b) => (a.class_num || 99) - (b.class_num || 99) || (a.race_time < b.race_time ? 1 : -1));
  return todayRaces[0];
}

export default function Landing() {
  const [featured, setFeatured] = useState(null); // { race, runners, raceMeta, prices }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const races = await getRacesToday();
        const pick = pickFeaturedRace(races);
        if (!pick || cancelled) { setLoading(false); return; }

        const cardData = await getRaceCard(pick.race_id);
        if (cancelled) return;

        let prices = {};
        try {
          const priceData = await getBetfairPrices(pick.race_id);
          prices = priceData.prices || {};
        } catch (e) { /* prices optional */ }

        setFeatured({
          race: cardData.race,
          runners: cardData.runners,
          raceMeta: pick,
          prices,
        });
      } catch (e) {
        console.error("Featured race fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <SEO path="/" description="Free horse racing analytics for punters who think. Tissue prices, going bias, trainer stats, pace maps and staking tools, all free." />
      {/* Hero */}
      <section className="hero-grid">
        <div style={{ position: "relative", overflow: "hidden" }}>
          <img
            src="/images/hero.webp"
            alt="Racing notebook"
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(90deg, transparent 60%, var(--cream) 100%)",
            pointerEvents: "none",
          }} />
        </div>

        <div className="hero-text">
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "repeating-linear-gradient(transparent 0px, transparent 31px, rgba(107,101,96,0.06) 31px, rgba(107,101,96,0.06) 32px)",
          }} />

          {/* Simon Nott interview — sends traffic to his (affiliate-supported) site */}
          <div className="fade-up" style={{
            position: "relative", width: "100%", marginBottom: 16,
            background: "rgba(201,162,39,0.10)",
            border: "1px solid rgba(201,162,39,0.30)",
            borderLeft: "3px solid var(--gold)",
            borderRadius: 8, padding: "12px 16px",
            fontSize: "0.98rem", color: "var(--ink)", lineHeight: 1.5,
          }}>
            Simon Nott very kindly took time out of his busy schedule to interview me.{" "}
            <a
              href="https://www.simonnott.co.uk/blog-racing/chris-ness-value-betting-analyst-simon-nott-zooms-in-41"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--terracotta)", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              Watch the interview &rarr;
            </a>
          </div>

          {/* Heads-up: paywall + book */}
          <div className="fade-up" style={{
            position: "relative", width: "100%", marginBottom: 16,
            background: "linear-gradient(135deg, rgba(198,123,78,0.08) 0%, rgba(201,162,39,0.08) 100%)",
            border: "1px solid rgba(198,123,78,0.25)",
            borderLeft: "3px solid var(--terracotta)",
            borderRadius: 8, padding: "14px 16px",
          }}>
            <div style={{
              fontFamily: "var(--font-hand)", fontSize: "1.2rem",
              color: "var(--terracotta)", marginBottom: 6, fontWeight: 700,
            }}>
              Out Now
            </div>
            <p style={{
              fontSize: "1.05rem", color: "var(--ink)", lineHeight: 1.55, margin: "0 0 10px 0",
            }}>
              <em>Mispriced &mdash; How Horse Racing Markets Work and How to Beat Them</em>,
              the book behind the framework, is now available on Amazon.{" "}
              <a
                href="https://www.amazon.co.uk/dp/B0GX3B67WH"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--terracotta)", fontWeight: 600 }}
              >
                Buy it &rarr;
              </a>{" "}
              &middot;{" "}
              <Link to="/book" style={{ color: "var(--terracotta)", fontWeight: 600 }}>
                Synopsis
              </Link>
            </p>
            <p style={{
              fontSize: "0.95rem", color: "var(--pencil)", lineHeight: 1.5, margin: 0,
            }}>
              Coming next for subscribers: <strong style={{ color: "var(--ink)" }}>Ask the Notebook</strong> &mdash; our AI-powered race analyst &mdash; alongside an enhanced Tissue Pricer, Going-Bias and full Backtest archive.
            </p>
          </div>

          {/* Headline + quick-link buttons */}
          <div className="fade-up fade-up-1" style={{ position: "relative", width: "100%", marginBottom: 20 }}>
            <h1 style={{
              fontFamily: "var(--font-display)", fontSize: "2rem",
              lineHeight: 1.15, marginBottom: 20, color: "var(--ink)", fontWeight: 700,
            }}>
              Let's Beat the{" "}
              <span style={{ position: "relative" }}>
                Market.
                <span style={{
                  position: "absolute", bottom: 2, left: 0, right: 0,
                  height: 3, background: "var(--gold)", borderRadius: 2,
                }} />
              </span>
            </h1>

            <div className="hero-quick-links">
              <Link to="/tools/todays-card" className="hero-quick-btn">
                <span className="hero-quick-icon">&#9632;</span>
                Today's Cards
              </Link>
              <Link to="/tools/tissue" className="hero-quick-btn">
                <span className="hero-quick-icon">&#9632;</span>
                Today's Tissue
              </Link>
              <Link to="/tools/trainer" className="hero-quick-btn">
                <span className="hero-quick-icon">&#9632;</span>
                Today's Trainers
              </Link>
              <Link to="/tools/jockey" className="hero-quick-btn">
                <span className="hero-quick-icon">&#9632;</span>
                Today's Jockeys
              </Link>
            </div>
          </div>

          {/* Cheltenham featured race (or fallback) */}
          {loading ? (
            <div className="fade-up fade-up-2" style={{ position: "relative" }}>
              <p style={{ color: "var(--pencil)", fontSize: "1rem" }}>Loading today's feature race...</p>
            </div>
          ) : featured ? (
            <div className="fade-up fade-up-2" style={{ position: "relative", width: "100%" }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  fontFamily: "var(--font-hand)", fontSize: "1.1rem",
                  color: "var(--terracotta)", marginBottom: 4,
                }}>
                  Featured Race
                </div>
                <h2 style={{
                  fontFamily: "var(--font-display)", fontSize: "1.3rem",
                  lineHeight: 1.2, color: "var(--ink)", fontWeight: 700, marginBottom: 6,
                }}>
                  {featured.raceMeta.race_time} {featured.raceMeta.course_name}
                </h2>
                <div style={{
                  fontSize: "0.85rem", color: "var(--pencil)",
                  display: "flex", gap: 12, flexWrap: "wrap",
                }}>
                  <span>{featured.raceMeta.race_type}</span>
                  <span>{featured.raceMeta.distance}</span>
                  <span>{featured.raceMeta.going_desc}</span>
                  <span>{featured.runners.length} runners</span>
                </div>
              </div>

              <div style={{
                maxHeight: "calc(100vh - 360px)", overflowY: "auto", overflowX: "auto",
                margin: "0 -20px", padding: "0 20px",
              }}>
                <CardTable
                  runners={featured.runners}
                  race={featured.race}
                  prices={featured.prices}
                />
              </div>

              <div style={{ marginTop: 12 }}>
                <Link
                  to="/tools/todays-card"
                  className="btn btn-primary"
                  style={{ fontSize: "0.9rem" }}
                >
                  All Today's Races &rarr;
                </Link>
              </div>
            </div>
          ) : (
            /* Fallback when no Cheltenham races today */
            <div className="fade-up fade-up-2" style={{ position: "relative" }}>
              <p style={{
                fontFamily: "var(--font-hand)", fontSize: "1.2rem", color: "var(--terracotta)",
                maxWidth: 440, lineHeight: 1.6, marginBottom: 20,
              }}>
                Data, not hunches.
              </p>
              <Link to="/articles" style={{
                display: "inline-block", textDecoration: "none", fontFamily: "var(--font-display)",
                fontSize: "0.95rem", color: "var(--ink)", padding: "14px 22px",
                background: "var(--paper)", border: "2px solid rgba(107,101,96,0.15)", borderRadius: 10,
                transition: "border-color 0.2s, transform 0.2s",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--turf)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(107,101,96,0.15)"; e.currentTarget.style.transform = ""; }}
              >
                The Market Is Efficient. Not Perfect. &rarr;
              </Link>
            </div>
          )}
        </div>
      </section>

      <div className="section-divider" />

      {/* CTA */}
      <section className="hero-cta-section">
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(ellipse at 50% 50%, rgba(201, 162, 39, 0.06) 0%, transparent 60%)",
        }} />
        <div style={{ maxWidth: 560, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ fontFamily: "var(--font-hand)", fontSize: "1.4rem", color: "var(--terracotta)", marginBottom: 10 }}>Ready?</div>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: "2.2rem", color: "var(--ink)", marginBottom: 14, lineHeight: 1.25 }}>
            Open the Notebook
          </h2>
          <p style={{ color: "var(--pencil)", fontSize: "1rem", marginBottom: 28, lineHeight: 1.7 }}>
            The core tools are free. No account required for Today's Cards, the
            Tissue Pricer and the Calculator. Register for a free account to
            access Trainer and Jockey Profiles.
          </p>
          <Link to="/tools/todays-card" className="btn btn-primary">Start Exploring</Link>
        </div>
      </section>
    </>
  );
}
